import { FunctionComponent, ReactElement, SVGProps } from "react"
import { useNavigate } from "react-router-dom"
import { Alert, Box, Button, Checkbox, CircularProgress, Collapse, Grid, Paper, Typography, useMediaQuery, useTheme } from "@mui/material"
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { CardCollapse, PixCollapse, BoletoCollapse } from "../collapses"
import { ReactComponent as Mastercard } from '../../../../../assets/icons/cardBrands/mastercard.svg'
import { ReactComponent as Visa } from '../../../../../assets/icons/cardBrands/visa.svg'
import { ReactComponent as Elo } from '../../../../../assets/icons/cardBrands/elo.svg'
import { ReactComponent as Hipercard } from '../../../../../assets/icons/cardBrands/hipercard.svg'
import { ReactComponent as DinersClub } from '../../../../../assets/icons/cardBrands/dinersClub.svg'
import { ReactComponent as AmericanExpress } from '../../../../../assets/icons/cardBrands/americanExpress.svg'
import { ReactComponent as PixIcon } from '../../../../../assets/icons/pixIcon.svg'
import { ReactComponent as BoletoIcon } from '../../../../../assets/icons/boletoIcon.svg'
import { ReactComponent as Checked } from '../../../../../assets/icons/cardChecked.svg'
import { ReactComponent as Unchecked } from '../../../../../assets/icons/cardUnchecked.svg'
import { useForm } from "../../../../../Form"
import { usePlan } from "../../../../../Plans"
import { interpolate, useTranslation } from "../../../../../i18n"
import CheckboxDefault from "../../../../CheckboxDefault"
import useCountdown from "../../../../../hooks/useCountdown"
import { limparCheckout } from "../../../../../utils/card/checkoutPersistido"
import { useSettings } from "../../../../../Settings"

interface PaymentMethodCardsProps {
  name: "PIX" | "CARTAO" | "BOLETO"  | "UNIXCARD"
  title: string
  description: string
  icons: FunctionComponent<SVGProps<SVGSVGElement>>[]
  detail: string
  detailVisibility: string
  collapse: ReactElement
  collapseIn: boolean
}

interface PaymentMethodsProps {
  text: string[]
  paymentStep: boolean
  noStock: boolean
}

export default function PaymentMethods({ text, paymentStep, noStock }: PaymentMethodsProps) {
  const { virtualChip, step } = usePlan()
  const { currentFields, setModelledField } = useForm()
  const {
    loadingSummary,
    errorSummary,
    sdk3DSIndisponivel,
    paymentMethod,
    keepCardChecked,
    cardMethodDisabled,
    keepCardDisabled,
    purchaseSummaryWithOtp,
  } = currentFields
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pageTitle } = useSettings()
  const pixData = JSON.parse(sessionStorage.getItem('pixData') || '{}')
  const theme = useTheme()
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"))
  
  const { formatado, expirado } = useCountdown(purchaseSummaryWithOtp?.expira_em ?? "")

  const IS_UNIXBANK = pageTitle === "Unixbank Telecom"

  const paymentMethodCards: PaymentMethodCardsProps[] = [
    IS_UNIXBANK && {
      name: 'UNIXCARD',
      title: 'UnixCard',
      description: t.page2.payment.unixCardDescription,
      icons: [Mastercard],
      detail: text[2],
      detailVisibility: 'flex',
      collapse: <CardCollapse variables={{text: text.slice(4, 9)}} />,
      collapseIn: paymentMethod === "UNIXCARD"
    },
    !cardMethodDisabled && {
      name: 'CARTAO',
      title: text[0],
      description: text[1],
      icons: [Mastercard, Visa, Elo, Hipercard, DinersClub, AmericanExpress],
      detail: IS_UNIXBANK ? text[11] : text[2],
      detailVisibility: text[3],
      collapse: <CardCollapse variables={{text: text.slice(4, 9)}} />,
      collapseIn: paymentMethod === "CARTAO"
    },
    {
      name: 'PIX',
      title: text[9],
      description: text[10],
      icons: [PixIcon],
      detail: text[11],
      detailVisibility: text[12],
      collapse: <PixCollapse variables={{text: text.slice(13, 20)}} />,
      collapseIn: paymentMethod === "PIX" && !pixData.pixLoading
    },
    {
      name: 'BOLETO',
      title: text[20],
      description: text[21],
      icons: [BoletoIcon],
      detail: text[22],
      detailVisibility: text[23],
      collapse: <BoletoCollapse variables={{text: text.slice(24)}} />,
      collapseIn: paymentMethod === "BOLETO"
    }
  ].filter(Boolean) as PaymentMethodCardsProps[]

  const refazerCompra = () => {
    limparCheckout()
    navigate('/')
  }

  const handleKeepCardChange = () => {
    if (paymentMethod === "CARTAO") {
      setModelledField('keepCardChecked', !keepCardChecked)
    }
  }

  const handleCheckboxChange = (checkbox: "PIX" | "CARTAO" | "BOLETO" | "UNIXCARD" | "DISABLED") => {
    setModelledField('paymentMethod', checkbox)
  }

  const renderErrorSummary = () => {
    return (
      <Paper elevation={0} sx={{ p: 4, textAlign: "center", border: "1px solid", borderColor: "divider" }}>
        <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
          {errorSummary}
        </Alert>
        <Button variant="contained" onClick={refazerCompra} sx={{ borderRadius: 0 }}>
          {t.page2.payment.redoPurchase}
        </Button>
      </Paper>
    )
  }

  const renderPaymentMethodCards = () => {
    return (
      paymentMethodCards
        .filter(({ name }) => !paymentStep || name === paymentMethod)
        .map(({
          name,
          title,
          description,
          icons,
          detail,
          detailVisibility,
          collapse,
          collapseIn
        }, index) => (
          <Grid key={`${index}-${paymentStep}`}>
            <Grid
              container
              className={`paymentCard ${paymentMethod === name ? 'activated' : 'disabled'}`}
              onClick={noStock ? () => {} : () => handleCheckboxChange(name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: noStock ? 'default' : 'pointer',
                borderRadius: '12px',
                p: { xs: '20px', md: '10px', lg: '20px' },
                opacity: noStock ? 0.5 : 1
              }}
            >
              <Grid item className="cardCheckbox" sx={{ mr: 1 }}>
                <CheckboxDefault checked={paymentMethod === name} checkedIcon={<Checked />} uncheckedIcon={<Unchecked />} />
              </Grid>
              <Grid item sx={{ display: 'flex', flexDirection: 'column', flex: '1' }}>
                <Grid item>
                  <Grid sx={{ display: 'flex', width: '100%' }}>
                    <Typography
                      variant="h6"
                      dangerouslySetInnerHTML={{ __html: title }}
                      sx={{ fontSize: { xs: 16 }, alignSelf: 'center' }}
                    />
                    <Box
                      className="cardDetail"
                      sx={{
                        display: detailVisibility,
                        justifyContent: 'center',
                        alignItems: 'center',
                        alignSelf: 'center',
                        fontSize: { xs: 10 },
                        fontWeight: '600',
                        color: 'white',
                        padding: '2px 6px',
                        ml: 1.25,
                        borderRadius: '5px'
                      }}>{detail}</Box>
                  </Grid>
                  <Typography
                    variant="body2"
                    dangerouslySetInnerHTML={{ __html: description }}
                    sx={{ fontSize: { xs: 12 } }}
                  />
                </Grid>
                {!keepCardDisabled && name === 'CARTAO' && paymentStep && virtualChip && (
                  <Grid className="keepCard" sx={{ display: 'flex', alignItems: 'center', mt: { xs: 1 } }}>
                    <Checkbox
                      color="secondary"
                      icon={<CheckBoxOutlineBlankIcon color="secondary" />}
                      checked={keepCardChecked}
                      onChange={handleKeepCardChange}
                      disabled={paymentMethod === "PIX"}
                      sx={{ p: 0, pr: { md: 0.5 }, '& svg': { width: { xs: '75%' } }, '&.MuiButtonBase-root.MuiCheckbox-root.Mui-disabled': { opacity: 0.3 } }}
                    />
                    <Typography variant="body2" sx={{ textAlign: 'center', fontSize: { xs: 10, md: 12 } }} dangerouslySetInnerHTML={{ __html: t.page2.payment.keepRecurring }} />
                  </Grid>
                )}
              </Grid>
              {(name !== 'UNIXCARD') && (name !== 'CARTAO' || lgUp) && (
                <Grid item className={`${name}-card-icons`}>
                  {icons.map((Icon, index) => <Icon key={index} />)}
                </Grid>
              )}
            </Grid>
            {paymentStep && (
              <Collapse className="collapses" in={collapseIn}>
                {collapse}
              </Collapse>
            )}
          </Grid>
        ))
    )
  }

  const renderStep5 = () => {
    if (loadingSummary) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
    if (errorSummary) return renderErrorSummary()
    return (
      <>
        {renderPaymentMethodCards()}
        {paymentMethod === "CARTAO" && sdk3DSIndisponivel && (
          <Alert severity="error">
            {t.errors.threeDSUnavailable}
          </Alert>
        )}
        {expirado ? (
          <>
            <Alert severity="warning">
              {t.page2.payment.paymentDeadlineExpired}
            </Alert>
            <Button variant="contained" onClick={refazerCompra} sx={{ borderRadius: 0, p: "16px 26px" }}>
              {t.page2.payment.redoPurchase}
            </Button>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            {interpolate(t.page2.payment.payWithin, { tempo: formatado })}
          </Typography>
        )}
      </>
    )
  }
  
  return (
    <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {(step === 4 && paymentMethod === 'CARTAO') ? renderStep5() : renderPaymentMethodCards()}
    </Grid>
  )
}
